/**
 * TURNING PUSH ON FOR ONE AGENT, from the phone.
 *
 * Owner, 2026-09-18: 「可以在Agent的設定頁面…設定說我需要這個Agent做push notification,
 * 或是不要…可以只讓少數的Agent回報 push notification,而不會淹沒使用者的整個螢幕」
 *
 * The preference itself is account-level and lives on the server (per
 * device_id + channel_hash, because a channel hash is not unique across
 * machines). What is native here is only the permission and the token: iOS has
 * to be asked, and the resulting APNs token has to reach the BFF.
 *
 * THE PLUGIN OBJECT IS NEVER RETURNED OR AWAITED. Capacitor's plugin object is a
 * Proxy that answers every property name with a method wrapper — `then`
 * included — so returning it from an async function makes it a thenable whose
 * `then` never calls back, and the promise never settles. That was the 2026-09-12
 * TestFlight paywall hang, and checkout.ts carries the full account. Everything
 * below hands it back INSIDE an object, and only ever calls methods on it.
 */

import {
  pushDeviceRegister,
  pushDeviceUnregister,
  pushPrefSet,
  pushPrefsGet,
  roomPushGet,
  roomPushSet,
} from '../transport/relay'

/**
 * WHICH SERVER THESE CALLS REACH — the bug this module was born with.
 *
 * avg-devapp-appstore found it on the owner's phone (2026-09-18): the switch kept
 * failing after the BFF had rolled out, because `api` from transport/http RELAYS
 * every call to a paired machine's edge, and the edge has no /account/push. The
 * tell was the word: the sheet said `not_found`, which is an EDGE's vocabulary,
 * where the BFF's is `unknown_route`.
 *
 * bffJson's own comment in relay.ts already said this would happen. So these go
 * through the shared BFF lane beside every other /account/* call, and nothing in
 * this file touches fetch or the relay api directly.
 */

type CapacitorGlobal = {
  isPluginAvailable?: (name: string) => boolean
  isNativePlatform?: () => boolean
}

function capacitor(): CapacitorGlobal | null {
  const c = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor
  return c && typeof c === 'object' ? c : null
}

/**
 * THE MAC SHELL IS NOT CAPACITOR — it is a bare WKWebView, so
 * isPluginAvailable() is false there and the switch would hide on a machine that
 * can perfectly well receive notifications (owner, 2026-09-18: 幫我把 Mac app
 * 也放上這個功能).
 *
 * avg-devapp-appstore gave the page a promise-returning message handler instead,
 * answering only our own origin's main frame. Its presence IS the capability
 * test, the same way isPluginAvailable is for the phone.
 */
type MacBridge = { postMessage: (m: { op: string }) => Promise<unknown> }

function macBridge(): MacBridge | null {
  const h = (window as unknown as {
    webkit?: { messageHandlers?: { avengentsPush?: MacBridge } }
  }).webkit?.messageHandlers?.avengentsPush
  return h && typeof h.postMessage === 'function' ? h : null
}

/** What a shell says about itself. Taken AS GIVEN — see registerOnThisDevice. */
type Registration = {
  token: string
  bundle_id: string
  environment: string
  platform: string
}

/**
 * Can this build push at all?
 *
 * The 1.0 store build ships no plugin and answers false — so the toggle is
 * HIDDEN there rather than shown broken. A control that cannot work is worse
 * than an absent one: it invites a tap and then explains nothing.
 */
export function pushSupported(): boolean {
  const c = capacitor()
  return !!c?.isPluginAvailable?.('PushNotifications') || !!macBridge()
}

/** What a build is called, from its own user agent: `AvengentsId/<bundle id>`. */
export function bundleId(): string {
  const m = /AvengentsId\/([A-Za-z0-9.\-]+)/.exec(navigator.userAgent || '')
  return m?.[1] ?? ''
}

/**
 * Which APNs world this build's tokens belong to.
 *
 * Nothing can read this off a token — it carries no marking — so it is derived
 * from the bundle, which is the one thing that reliably differs: the Dev app is
 * always Xcode-signed (aps-environment development), and since 2026-09-16 the
 * store bundle ships only through TestFlight and the App Store.
 *
 * A wrong answer is recoverable rather than silent: the server retries the other
 * gateway once on BadDeviceToken and keeps the corrected environment.
 */
export function apnsEnvironment(bundle = bundleId()): 'sandbox' | 'production' {
  return bundle.endsWith('.dev') ? 'sandbox' : 'production'
}

export type PushPermission = 'granted' | 'denied' | 'prompt' | 'unsupported'

type PushPlugin = {
  checkPermissions: () => Promise<{ receive: string }>
  requestPermissions: () => Promise<{ receive: string }>
  register: () => Promise<void>
  addListener: (name: string, cb: (x: never) => void) => Promise<unknown> | unknown
}

/** Inside an object, never as the value itself — see the header. */
async function plugin(): Promise<{ p: PushPlugin } | null> {
  if (!pushSupported()) return null
  const mod = await import('@capacitor/push-notifications')
  return { p: mod.PushNotifications as unknown as PushPlugin }
}

/**
 * Ask iOS, then register, then hand the token to the BFF.
 *
 * THE LISTENER IS ADDED BEFORE register(), not after. `register()` is what makes
 * APNs produce the token, and on a warm start the 'registration' event can land
 * before the next line of this function runs — a listener attached afterwards
 * would miss it, and the toggle would sit there having been granted permission
 * and never delivered a token.
 */
/**
 * Ask the shell for a token, however this shell does that, and hand it to the BFF.
 *
 * TWO SHELLS, ONE SWITCH. The phone is Capacitor; the Mac is a bare WKWebView
 * with a message handler. They differ in how a token is obtained and in nothing
 * else — the preference, the registration and the BFF call are identical, which
 * is why this branches here and not in the component.
 *
 * THE SHELL'S OWN ANSWER IS TAKEN AS GIVEN. On the Mac, `environment` is read
 * from the binary's aps entitlement, so a Developer ID release reports
 * `production` without anyone deriving it from a bundle name — which is strictly
 * better than the phone's suffix rule, and the reason this does not try to be
 * clever on its behalf.
 */
export async function enablePushOnThisDevice(): Promise<PushPermission> {
  const mac = macBridge()
  const reg = mac ? await macRegister(mac) : await capacitorRegister()
  if (typeof reg === 'string') return reg          // a permission verdict, not a token
  await pushDeviceRegister(reg.token, reg.bundle_id, reg.environment, reg.platform)
  return 'granted'
}

/**
 * KEEP THE SERVER'S TOKEN FOR THIS DEVICE CURRENT — avg-devapp-appstore, 2026-09-24,
 * after a notify to the owner's Mac reported "notified 1 device(s)" and arrived nowhere:
 * they had just reinstalled the app, its APNs token changed, and nothing ever told us.
 *
 * THE DEFECT, and it is structural rather than macOS's: `pushDeviceRegister` was
 * reachable from exactly two places in the whole app — the onChange of the two push
 * switches. A device's token therefore reached the server ONLY when a person flipped a
 * switch, so any token that changed underneath (a reinstall, a restore, APNs' own
 * rotation) was never corrected. We keep sending to a dead token, APNs answers
 * BadDeviceToken, the row is dropped, and the person sees nothing — with no symptom
 * anywhere in the app. Not a Mac bug: the phone has the identical shape, and a reinstall
 * there would look exactly like "push stopped working for no reason".
 *
 * CHECK, NEVER REQUEST. This runs at every launch, so it must not be able to raise the
 * system permission dialog: a prompt on startup, at someone who never asked for
 * notifications, would be a worse bug than the one being fixed. The Capacitor path reads
 * `checkPermissions()` and returns unless the answer is already `granted` — which is
 * also the correct test for "this device was deliberately set up for push", so it
 * doubles as the opt-in check without a round trip.
 *
 * The Mac path asks its shell for the token, which is safe for the same reason by a
 * different route: that shell registers with APNs on launch of its own accord, so asking
 * for what it already holds adds no dialog, and its bridge REJECTS ('denied') rather
 * than prompting when the person has said no.
 *
 * A re-register of an UNCHANGED token is a no-op write — the BFF's device row is keyed
 * by the token — so the common case costs one request and changes nothing.
 *
 * SILENT ON EVERY FAILURE, like forgetPushOnThisDevice below: this is bookkeeping that
 * runs before anyone has asked for anything, and it must never surface an error or block
 * a render.
 */
export async function refreshPushRegistration(): Promise<void> {
  try {
    if (!pushSupported()) return
    const mac = macBridge()
    if (mac) {
      const r = (await mac.postMessage({ op: 'register' })) as Registration
      if (!r?.token) return
      await pushDeviceRegister(r.token, r.bundle_id, r.environment, r.platform)
      return
    }
    const got = await plugin()
    if (!got) return
    if ((await got.p.checkPermissions()).receive !== 'granted') return
    const reg = await capacitorRegister()
    if (typeof reg === 'string') return
    await pushDeviceRegister(reg.token, reg.bundle_id, reg.environment, reg.platform)
  } catch {
    /* bookkeeping: never surfaced, never fatal */
  }
}

/**
 * The Mac's half. Its bridge REJECTS rather than returning a verdict — 'denied',
 * 'apns_registration_timeout', 'push_not_in_this_build', or APNs' own words — so
 * the refusals a person can act on are normalised here to the same shape the
 * phone produces, and everything else is re-thrown with the shell's wording
 * intact.
 */
async function macRegister(mac: MacBridge): Promise<Registration | PushPermission> {
  try {
    const r = (await mac.postMessage({ op: 'register' })) as Registration
    if (!r?.token) throw new Error('apns_empty_token')
    return r
  } catch (e) {
    const why = String(e instanceof Error ? e.message : e)
    if (/denied/i.test(why)) return 'denied'
    if (/push_not_in_this_build/i.test(why)) return 'unsupported'
    throw e
  }
}

/** The phone's half, unchanged in behaviour — see the listener note below. */
async function capacitorRegister(): Promise<Registration | PushPermission> {
  const got = await plugin()
  if (!got) return 'unsupported'
  const { p } = got

  let perm = (await p.checkPermissions()).receive
  if (perm === 'prompt' || perm === 'prompt-with-rationale') {
    perm = (await p.requestPermissions()).receive
  }
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'prompt'

  /*
   * THE LISTENER IS ADDED BEFORE register(). `register()` is what makes APNs
   * produce the token, and on a warm start the 'registration' event can land
   * before the next line runs — a listener attached afterwards would miss it,
   * and the toggle would sit there permission-granted with no token delivered.
   */
  const token = await new Promise<string>((resolve, reject) => {
    // Bounded: a registration that never arrives must not leave the toggle
    // spinning, the way the image upload did before it learned the same rule.
    const timer = setTimeout(() => reject(new Error('apns_registration_timeout')), 15_000)
    void p.addListener('registration', (t: never) => {
      clearTimeout(timer)
      resolve(String((t as unknown as { value?: string }).value ?? ''))
    })
    void p.addListener('registrationError', (e: never) => {
      clearTimeout(timer)
      reject(new Error(String((e as unknown as { error?: string }).error ?? 'registration_error')))
    })
    void p.register()
  })
  if (!token) throw new Error('apns_empty_token')
  const bundle = bundleId()
  return {
    token,
    bundle_id: bundle,
    environment: apnsEnvironment(bundle),
    platform: 'ios',
  }
}

/** Read the account's ON set — absence is off, so only these come back. */
export async function pushPrefs(): Promise<Array<{ device_id: string; channel_hash: string }>> {
  const r = await pushPrefsGet()
  return r.prefs ?? []
}

export async function setPushPref(
  deviceId: string,
  channelHash: string,
  enabled: boolean,
): Promise<void> {
  await pushPrefSet(deviceId, channelHash, enabled)
}

/** Which chat rooms this person asked to be notified about. */
export async function roomPrefs(): Promise<string[]> {
  const r = await roomPushGet()
  return (r.rooms ?? []).map((x) => x.conv_id)
}

export async function setRoomPref(convId: string, enabled: boolean): Promise<void> {
  await roomPushSet(convId, enabled)
}

/**
 * A tap on a notification opens that agent.
 *
 * Bound AT BOOT, not when a panel mounts: on a cold start iOS delivers the tap
 * immediately after launch, and a listener attached later has already missed the
 * only event it existed for.
 *
 * The destination is the app's REAL url shape (`?agent=…&device=…`) rather than
 * a route invented for notifications — urlState.ts resolves that on a cold load,
 * which is exactly why a tap works before the app has finished starting.
 */
export async function armPushTaps(navigate: (path: string) => void): Promise<void> {
  /*
   * THE MAC CLAIMS ITS OWN TAP, and claiming it is the point.
   *
   * The shell dispatches a CANCELABLE `avengents:push-open`. If nobody calls
   * preventDefault() it falls back to location.assign(path) itself — which works,
   * but reloads the whole app to reach a route the SPA could have shown
   * instantly. Handling it here is the difference between a tap that opens an
   * agent and a tap that restarts the product first.
   */
  window.addEventListener('avengents:push-open', (ev: Event) => {
    const path = (ev as CustomEvent<{ path?: string }>).detail?.path
    if (typeof path !== 'string' || !path) return
    ev.preventDefault()
    navigate(path)
  })

  const got = await plugin()
  if (!got) return
  void got.p.addListener('pushNotificationActionPerformed', (ev: never) => {
    const data = (ev as unknown as { notification?: { data?: Record<string, unknown> } })
      .notification?.data
    const path = typeof data?.['path'] === 'string' ? data['path'] : ''
    if (path) navigate(path)
  })
}

/**
 * Stop this device receiving this account's notifications. Called on sign-out,
 * BEFORE the session cookie is destroyed — the unregister is authorised by it.
 *
 * Silent on every failure: a sign-out must never be blocked by a notification
 * cleanup, and a token left behind is dropped by Apple's own 410 on the next
 * send or by its TTL.
 */
export async function forgetPushOnThisDevice(): Promise<void> {
  try {
    const mac = macBridge()
    if (mac) {
      // The Mac shell knows its own token; it does not need to be told.
      await mac.postMessage({ op: 'unregister' })
      return
    }
    const got = await plugin()
    if (!got) return
    const token = await new Promise<string>((resolve) => {
      const timer = setTimeout(() => resolve(''), 3_000)
      void got.p.addListener('registration', (t: never) => {
        clearTimeout(timer)
        resolve(String((t as unknown as { value?: string }).value ?? ''))
      })
      void got.p.register()
    })
    if (token) await pushDeviceUnregister(token)
  } catch {
    /* sign-out proceeds regardless — see the note at the call site */
  }
}
